import { Component, OnInit, OnDestroy, HostListener, ChangeDetectorRef } from '@angular/core';
import { AuthService } from 'src/app/core/services/auth.service';
import { IncomingCallService } from 'src/app/core/services/incoming-call.service';
import { SocketService } from 'src/app/core/services/socket.service';
import { environment } from 'src/environments/environment';

export type CallPhase = 'incoming';
export type CallType = 'video' | 'audio';

function arcPos(angleDeg: number, R: number): { tx: number; ty: number } {
  const a = (angleDeg * Math.PI) / 180;
  return { tx: Math.round(R * Math.sin(a)), ty: Math.round(R * -Math.cos(a)) };
}

const START = 195, STEP = 38;
const ANGLES = [0, 1, 2, 3, 4].map(i => START + i * STEP);

const INCOMING_BTNS = [
  { id: 'in-decline', ...arcPos(ANGLES[0], 138), delay: 0, icon: 'call_end', label: 'Decline', color: '#ffb4ab', bg: '#93000a', pulseClass: 'pulse-red' },
  { id: 'in-audio', ...arcPos(ANGLES[1], 138), delay: 55, icon: 'mic', label: 'Audio', color: '#bbc3ff', bg: '#303f9f', pulseClass: '' },
  { id: 'in-hold', ...arcPos(ANGLES[2], 138), delay: 90, icon: 'pause_circle', label: 'Hold', color: '#44d8f1', bg: '#1a2a4a', pulseClass: 'pulse-blue' },
  { id: 'in-facetime', ...arcPos(ANGLES[3], 138), delay: 125, icon: 'videocam', label: 'Video', color: 'rgba(57,255,20,0.9)', bg: '#1a5c00', pulseClass: 'pulse-green' },
  { id: 'in-myroon', ...arcPos(ANGLES[4], 138), delay: 160, icon: 'spatial_audio_off', label: 'MyRoon', color: '#cebdff', bg: '#1a0a35', pulseClass: '' },
];

@Component({
  selector: 'app-incoming-call',
  templateUrl: './incoming-call.component.html',
  styleUrls: ['./incoming-call.component.css']
})
export class IncomingCallComponent implements OnInit, OnDestroy {
  public show: boolean = false;
  public caller: any = null;
  public interval: any = null;
  public isVideoEnabled: any = null;
  private audio: HTMLAudioElement | null = null;

  public icallSub$: any = null;
  public outcallreject$: any = null;

  // Drag logic properties
  public isDragging = false;
  public popupPos = { x: 0, y: 0 };
  private _dragStart = { x: 0, y: 0 };
  private _posStart = { x: 0, y: 0 };
  public hasMoved = false;

  // Bubble Logic Properties
  public phase: CallPhase = 'incoming';
  public menuOpen = false;
  public incomingBtns = INCOMING_BTNS;
  private autoClose: any = null;

  constructor(
    private _ics: IncomingCallService,
    private _ss: SocketService,
    public _as: AuthService,
    private cdr: ChangeDetectorRef
  ) {
    this.icallSub$ = this._ics.incomingCallSubject.subscribe((res: any) => {
      if (res !== null) {
        this.isVideoEnabled = res.isVideoEnabled;
        this.caller = res.fromUserObj || { _id: res.callerId };
        this.show = true;
        this.phase = 'incoming';
        this.hasMoved = false;
        this.menuOpen = false;
        this.playAudio();
        this.cdr.detectChanges();
      }
    });

    this.outcallreject$ = this._ics.outgoingCallRejectEventSubject.subscribe((res: any) => {
      if (res !== null) {
        this.reset();
      }
    });
  }

  ngOnInit(): void { }

  ngOnDestroy(): void {
    if (this.caller) {
      this.reject();
    }
    this.icallSub$.unsubscribe();
    this.outcallreject$.unsubscribe();
    this.stopAudio();
  }

  /* --- Dragging --- */
  public onDragStart(event: MouseEvent): void {
    if ((event.target as HTMLElement).closest('.cw-abtn') || (event.target as HTMLElement).closest('.cw-bubble')) {
      return;
    }
    this.isDragging = true;
    this.hasMoved = true;
    this._dragStart = { x: event.clientX, y: event.clientY };
    this._posStart = { x: this.popupPos.x, y: this.popupPos.y };
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  public onDragMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    const dx = event.clientX - this._dragStart.x;
    const dy = event.clientY - this._dragStart.y;
    this.popupPos = { x: this._posStart.x + dx, y: this._posStart.y + dy };
  }

  @HostListener('document:mouseup')
  public onDragEnd(): void {
    this.isDragging = false;
  }

  /* --- Audio --- */
  public playAudio() {
    this.stopAudio();
    this.audio = new Audio();
    this.audio.src = environment.webUrl + "assets/audio/incoming.wav";
    this.audio.load();
    this.audio.play().catch(err => console.error("Error playing audio:", err));

    this.interval = setInterval(() => {
      if (this.audio) {
        this.audio.currentTime = 0;
        this.audio.play().catch(err => console.error("Error repeating audio:", err));
      }
    }, 3000);
  }

  private stopAudio() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
      this.audio = null;
    }
  }

  /* --- Actions --- */
  public reject() {
    if (this.caller) {
      this._ss.emitIncomingCallRejectEvent(this.caller._id);
    }
    this.reset();
  }

  // Changed to generic accept accepting isVideo argument for bubble handlers
  public accept(isVideo: boolean) {
    if (this.caller) {
      this._ss.setCurrentUser(this._as.user);
      this._ss.emitIncomingCallAcceptEvent(this.caller, isVideo);
    }
    this.reset();
  }

  public getProfileImage(user: any): string | null {
    if (!user) return null;
    const profileImage = typeof user?.profileImage === 'string' ? user.profileImage.trim() : '';
    return profileImage ? profileImage : 'assets/images/user-login.svg';
  }

  public reset() {
    this.stopAudio();
    this.caller = null;
    this.show = false;
    this.closeMenu();
    this.cdr.detectChanges();
  }

  /* --- Bubble UI Logic --- */
  get phaseLabel(): string {
    return this.isVideoEnabled ? 'Incoming FaceTime' : 'Incoming Call';
  }
  get subLabel(): string { return 'Connecting...'; }
  get statusText(): string { return `Incoming · ${this.isVideoEnabled ? 'Video' : 'Audio'}`; }
  get incomingOpen(): boolean { return this.menuOpen; }
  get pulseActive(): boolean { return true; }
  get bubbleGlow(): string { return '0 0 32px rgba(206,189,255,.4),0 8px 40px rgba(0,0,0,.7)'; }
  get bubbleTransform(): string { return 'scale(1)'; }
  get progressDuration(): string { return '2.8s'; }

  onBubbleTap(): void {
    this.menuOpen ? this.closeMenu() : this.openMenu(2800);
  }

  handleIncoming(id: string): void {
    switch (id) {
      case 'in-decline':
        this.reject();
        break;
      case 'in-audio':
        this.accept(false);
        break;
      case 'in-facetime':
        this.accept(true);
        break;
      case 'in-hold':
        this.closeMenu(); break; // hold logic can be added later
      case 'in-myroon':
        this.closeMenu(); break;
    }
  }

  btnTransform(btn: any, visible: boolean): string {
    return visible ? `translate(calc(-50% + ${btn.tx}px), calc(-50% + ${btn.ty}px)) scale(1)` : 'translate(-50%,-50%) scale(0.4)';
  }
  btnTransition(delay: number): string { return `opacity .22s ${delay / 1000}s, transform .4s cubic-bezier(.34,1.56,.64,1) ${delay / 1000}s`; }

  private openMenu(ms: number): void {
    this.menuOpen = true;
    clearTimeout(this.autoClose);
    this.autoClose = setTimeout(() => { this.menuOpen = false; this.cdr.detectChanges(); }, ms);
    this.cdr.detectChanges();
  }
  private closeMenu(): void { this.menuOpen = false; clearTimeout(this.autoClose); this.cdr.detectChanges(); }
  trackById(_: number, item: any): string { return item.id; }
}
